import { Footer } from "@/components/footer"
import { FileText, Shield, Users, CreditCard, AlertCircle, Lock } from "lucide-react"
import { SEO } from "@/components/SEO"

export default function TermsPage() {
  const sections = [
    {
      icon: Users,
      title: "1. Definitions",
      content: (
        <div className="space-y-3">
          <p><strong>Stay App</strong> – The mobile application, website, and related services.</p>
          <p><strong>User</strong> – Any person using Stay App.</p>
          <p><strong>Customer</strong> – A user who browses or books services.</p>
          <p><strong>Service Provider</strong> – A user or business offering services.</p>
          <p><strong>Services</strong> – Stays, Experiences, Mobility, Food & Nightlife, and Essentials.</p>
          <p><strong>Booking</strong> – A confirmed reservation or service request.</p>
        </div>
      ),
    },
    {
      icon: Shield,
      title: "2. Eligibility",
      content: (
        <div className="space-y-3">
          <p>You must:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Be at least 18 years old</li>
            <li>Provide accurate registration information</li>
            <li>Have legal capacity to enter a binding agreement</li>
          </ul>
        </div>
      ),
    },
    {
      icon: Lock,
      title: "3. Account Registration",
      content: (
        <div className="space-y-3">
          <p>To create an account, you may be required to provide:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Country/region phone number</li>
            <li>First name and surname</li>
            <li>Date of birth</li>
            <li>Password and password confirmation</li>
          </ul>
          <p>You are responsible for safeguarding your login details and all activity under your account.</p>
        </div>
      ),
    },
    {
      icon: Users,
      title: "4. User Roles",
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">4.1 Customers</h4>
            <p>Customers may:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Browse and book services</li>
              <li>Make payments</li>
              <li>Save favourites</li>
              <li>Rate and review services</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">4.2 Service Providers</h4>
            <p>Service Providers may:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Create and manage listings</li>
              <li>Set pricing and availability</li>
              <li>Accept or decline bookings</li>
              <li>Receive payments subject to platform fees</li>
            </ul>
          </div>
          <p>Users can switch roles without creating a new account.</p>
        </div>
      ),
    },
    {
      icon: FileText,
      title: "5. Nature of the Platform",
      content: (
        <div className="space-y-3">
          <p>Stay App is a technology platform that connects Customers with independent Service Providers.</p>
          <p>Stay App:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Does not own or operate listed services</li>
            <li>Is not a party to agreements between Customers and Service Providers</li>
            <li>Is not responsible for the quality or delivery of services</li>
          </ul>
        </div>
      ),
    },
    {
      icon: CreditCard,
      title: "6. Bookings & Payments",
      content: (
        <div className="space-y-3">
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Payments are processed via third-party payment providers</li>
            <li>Stay App may charge service or commission fees, disclosed before booking</li>
            <li>Prices and taxes are determined by Service Providers</li>
          </ul>
        </div>
      ),
    },
    {
      icon: AlertCircle,
      title: "7. Cancellations & Refunds",
      content: (
        <div className="space-y-3">
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Cancellation and refund policies are set by Service Providers</li>
            <li>Policies are shown before booking</li>
            <li>Refunds are not guaranteed unless required by law</li>
          </ul>
        </div>
      ),
    },
  ]

  const additionalSections = [
    {
      title: "8. User Conduct",
      content: (
        <div className="space-y-3">
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Provide false or misleading information</li>
            <li>Harass or abuse other users</li>
            <li>Upload unlawful or harmful content</li>
            <li>Bypass payments or platform fees</li>
            <li>Use the platform for illegal purposes</li>
          </ul>
          <p>Violations may result in suspension or termination.</p>
        </div>
      ),
    },
    {
      title: "9. Reviews & Content",
      content: (
        <div className="space-y-3">
          <p>By posting content or reviews, you:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Confirm the content is lawful and accurate</li>
            <li>Grant Stay App a non-exclusive right to use it</li>
            <li>Agree not to post defamatory or misleading material</li>
          </ul>
          <p>Stay App may remove content at its discretion.</p>
        </div>
      ),
    },
    {
      title: "10. Privacy & Data Protection",
      content: (
        <div className="space-y-3">
          <p>Your use of Stay App is governed by our Privacy Policy.</p>
          <p>By using the platform, you consent to data processing as described therein.</p>
        </div>
      ),
    },
    {
      title: "11. Intellectual Property",
      content: (
        <div className="space-y-3">
          <p>All intellectual property on Stay App belongs to Stay App or its licensors.</p>
          <p>You may not copy, modify, or distribute content without permission.</p>
        </div>
      ),
    },
    {
      title: "12. Platform Availability",
      content: (
        <div className="space-y-3">
          <p>Stay App does not guarantee uninterrupted access and may suspend services for:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Maintenance</li>
            <li>Security reasons</li>
            <li>Legal or regulatory requirements</li>
          </ul>
        </div>
      ),
    },
    {
      title: "13. Limitation of Liability",
      content: (
        <div className="space-y-3">
          <p>To the extent permitted by law:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Stay App is not liable for indirect or consequential damages</li>
            <li>Stay App is not responsible for actions of Service Providers</li>
            <li>Liability is limited to fees paid to Stay App in the last 12 months</li>
          </ul>
        </div>
      ),
    },
    {
      title: "14. Indemnity",
      content: (
        <div className="space-y-3">
          <p>You agree to indemnify Stay App against claims arising from:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Your use of the platform</li>
            <li>Breach of these Terms</li>
            <li>Violation of laws or third-party rights</li>
          </ul>
        </div>
      ),
    },
    {
      title: "15. Termination",
      content: (
        <div className="space-y-3">
          <p>Stay App may suspend or terminate accounts for:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Breach of these Terms</li>
            <li>Legal or regulatory requirements</li>
            <li>Risk to users or the platform</li>
          </ul>
          <p>Users may close their account at any time.</p>
        </div>
      ),
    },
    {
      title: "16. Changes to Terms",
      content: (
        <p>We may update these Terms periodically. Continued use of Stay App means you accept the updated Terms.</p>
      ),
    },
    {
      title: "17. Governing Law",
      content: (
        <p>These Terms are governed by the laws of the Republic of Botswana.</p>
      ),
    },
    {
      title: "18. Contact Information",
      content: (
        <div className="space-y-3">
          <p><strong>Stay App Support</strong></p>
          <p>Email: support@stayapp.com</p>
          <p>Address: [Insert Business Address]</p>
        </div>
      ),
    },
  ]

  return (
    <>
      <SEO
        title="Terms and Conditions - StayApp | Legal Terms & User Agreement"
        description="Read StayApp's Terms and Conditions. Understand your rights and responsibilities when using our platform for booking accommodation, travel, and lifestyle services."
        keywords="terms and conditions, user agreement, StayApp terms, legal terms, platform terms"
        ogImage="/logo.png"
        ogUrl="https://stayapp.com/terms"
        canonical="https://stayapp.com/terms"
      />
      <main className="pt-28 md:pt-36 lg:pt-40">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#75055B] via-[#5a0447] to-black text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 px-2">
            Terms and Conditions
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto mt-4 px-4">
            By accessing or using Stay App, you agree to these Terms and Conditions. If you do not agree, do not use the platform.
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8 sm:space-y-12">
              {/* Featured Sections with Icons */}
              {sections.map((section, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 hover:border-[#DB4789]/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#75055B]/10 flex items-center justify-center">
                      <section.icon className="w-6 h-6 text-[#75055B]" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-black flex-1">{section.title}</h2>
                  </div>
                  <div className="text-gray-700 leading-relaxed ml-16">{section.content}</div>
                </div>
              ))}

              {/* Additional Sections */}
              {additionalSections.map((section, index) => (
                <div
                  key={index + sections.length}
                  className="bg-white rounded-xl p-6 sm:p-8 border border-gray-200 hover:border-[#DB4789]/30 transition-all duration-300"
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-black mb-4">{section.title}</h2>
                  <div className="text-gray-700 leading-relaxed">{section.content}</div>
                </div>
              ))}
            </div>

            {/* Agreement Notice */}
            <div className="mt-12 sm:mt-16 bg-gradient-to-r from-[#75055B] to-[#DB4789] rounded-2xl p-6 sm:p-8 text-white">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold mb-2">Agreement to Terms</h3>
                  <p className="text-white/90 leading-relaxed">
                    By continuing to use Stay App, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these Terms, please discontinue use of the platform immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  )
}

